import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Tab,
  TabList,
  Table,
  Tabs,
  Typography,
} from '@mui/joy';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { DirectoryAccount } from '../accounts.model';
import { accountsService } from '../accounts.service';
import { formatGbp } from '../arrangements.model';
import { billStatusLabel, type AccountBill } from '../bills.model';
import { billsService } from '../bills.service';
import { BillActionsMenu, billPath } from '../components/BillActionsMenu';
import { BillFormModal, MarkPaidModal, SendBillModal, useBillToCompanies } from '../components/BillForms';

type TabFilter = 'current' | 'draft' | 'freeagent' | 'attention' | 'archive' | 'all';
const TABS: TabFilter[] = ['current', 'draft', 'freeagent', 'attention', 'archive', 'all'];

export const BILL_STATUS_COLOURS = {
  draft: 'neutral',
  sent: 'success',
  error: 'danger',
  archived: 'neutral',
} as const;

function inTab(bill: AccountBill, tab: TabFilter): boolean {
  switch (tab) {
    case 'current': return bill.status !== 'archived';
    case 'draft': return bill.status === 'draft';
    case 'freeagent': return bill.status === 'sent';
    case 'attention': return bill.status === 'error' || bill.freeagentOutOfDate;
    case 'archive': return bill.status === 'archived';
    default: return true;
  }
}

export function BillStatusChip({ bill }: { bill: AccountBill }) {
  return (
    <Chip
      size="sm"
      variant={bill.status === 'archived' ? 'outlined' : 'soft'}
      color={bill.freeagentOutOfDate ? 'warning' : BILL_STATUS_COLOURS[bill.status]}
      title={bill.syncError || undefined}
    >
      {billStatusLabel(bill)}
    </Chip>
  );
}

/** Finance → Bills: supplier and subcontractor invoices to us, sent to FreeAgent with CIS and VAT treatment. */
const BillsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [bills, setBills] = useState<AccountBill[]>([]);
  const [accounts, setAccounts] = useState<DirectoryAccount[]>([]);
  const billToCompanies = useBillToCompanies();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AccountBill | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [sending, setSending] = useState<AccountBill | null>(null);
  const [paying, setPaying] = useState<AccountBill | null>(null);
  const [search, setSearch] = useState('');

  const tab: TabFilter = TABS.includes(params.get('tab') as TabFilter) ? params.get('tab') as TabFilter : 'current';
  const accountFilter = params.get('account') || '';
  const subcontractorsOnly = params.get('subcontractors') === '1';
  const setParam = (key: string, value: string | null) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    }, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [billRows, accountRows] = await Promise.all([billsService.list(), accountsService.list()]);
      setBills(billRows);
      setAccounts(accountRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const accountsById = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
  const accountName = (id: string) => accountsById.get(id)?.companyName || 'Unknown account';
  const replace = (bill: AccountBill) => {
    setBills((current) => (current.some((row) => row.id === bill.id)
      ? current.map((row) => (row.id === bill.id ? bill : row))
      : [bill, ...current]));
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return bills.filter((bill) => {
      if (accountFilter && bill.accountId !== accountFilter) return false;
      if (subcontractorsOnly && bill.cisBand === 'none' && !bill.isLegacy) return false;
      if (!needle) return true;
      return [bill.reference, bill.description, accountsById.get(bill.accountId)?.companyName]
        .some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [accountFilter, accountsById, bills, search, subcontractorsOnly]);

  const counts = useMemo(
    () => Object.fromEntries(TABS.map((key) => [key, filtered.filter((bill) => inTab(bill, key)).length])) as Record<TabFilter, number>,
    [filtered]
  );
  const visible = filtered.filter((bill) => inTab(bill, tab));
  const billedAccounts = accounts.filter((account) => bills.some((bill) => bill.accountId === account.id));

  const afterSave = (bill: AccountBill) => {
    setFormOpen(false);
    replace(bill);
    if (bill.freeagentOutOfDate) setSending(bill);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
          <Stack spacing={0.5}>
            <Typography level="h3">Bills</Typography>
            <Typography level="body-sm" textColor="neutral.600" sx={{ maxWidth: 760 }}>
              Subcontractor and supplier invoices to us. Enter them as written and send them to FreeAgent at their full value. For CIS subcontractors, FreeAgent records the gross cost and the CIS owed to HMRC.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Button variant="outlined" color="neutral" component={Link} to="/cis-certificates">CIS summary</Button>
            <Button onClick={() => { setEditing(null); setFormOpen(true); }}>New bill</Button>
          </Stack>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Tabs value={tab} onChange={(_, value) => setParam('tab', value === 'current' ? null : String(value))} sx={{ bgcolor: 'transparent' }}>
            <TabList sx={{ flexWrap: 'wrap' }}>
              <Tab value="current">Current ({counts.current})</Tab>
              <Tab value="draft">Drafts ({counts.draft})</Tab>
              <Tab value="freeagent">In FreeAgent ({counts.freeagent})</Tab>
              <Tab value="attention" color={counts.attention > 0 ? 'danger' : undefined}>Needs attention ({counts.attention})</Tab>
              <Tab value="archive">Archive ({counts.archive})</Tab>
              <Tab value="all">All ({counts.all})</Tab>
            </TabList>
          </Tabs>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <Checkbox
              size="sm"
              label="CIS subcontractors"
              checked={subcontractorsOnly}
              onChange={(event) => setParam('subcontractors', event.target.checked ? '1' : null)}
            />
            <Select
              size="sm"
              value={accountFilter || null}
              placeholder="All accounts"
              onChange={(_, value) => setParam('account', value ? String(value) : null)}
              sx={{ minWidth: 200 }}
            >
              <Option value="">All accounts</Option>
              {billedAccounts.map((account) => <Option key={account.id} value={account.id}>{account.companyName}</Option>)}
            </Select>
            <Input size="sm" placeholder="Search invoice number or name" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: 240 }} />
          </Stack>
        </Stack>

        {error && <Alert color="danger">{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
            <Table hoverRow sx={{ minWidth: 960 }}>
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Date</th>
                  <th>From</th>
                  <th>Invoice</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th style={{ textAlign: 'right' }}>CIS</th>
                  <th style={{ textAlign: 'right' }}>We pay</th>
                  <th style={{ width: 150 }}>Status</th>
                  <th style={{ width: 48 }} />
                </tr>
              </thead>
              <tbody>
                {visible.map((bill) => (
                  <tr key={bill.id} onClick={() => navigate(billPath(bill.id))} style={{ cursor: 'pointer' }}>
                    <td>{bill.datedOn}</td>
                    <td>
                      {accountName(bill.accountId)}
                      {bill.description && <Typography level="body-xs" textColor="neutral.500" display="block" noWrap>{bill.description}</Typography>}
                    </td>
                    <td>{bill.reference}</td>
                    <td style={{ textAlign: 'right' }}>{formatGbp(bill.totals.grossTotal)}</td>
                    <td style={{ textAlign: 'right' }}>{bill.totals.cisDeduction > 0 ? `− ${formatGbp(bill.totals.cisDeduction)}` : '—'}</td>
                    <td style={{ textAlign: 'right' }}>{formatGbp(bill.totals.payableTotal)}</td>
                    <td><BillStatusChip bill={bill} /></td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <BillActionsMenu
                        bill={bill}
                        onEdit={(row) => { setEditing(row); setFormOpen(true); }}
                        onSend={setSending}
                        onMarkPaid={setPaying}
                        onChanged={replace}
                        onDeleted={(row) => setBills((current) => current.filter((item) => item.id !== row.id))}
                      />
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <Typography level="body-sm" textColor="neutral.500" sx={{ py: 2, textAlign: 'center' }}>
                        {bills.length === 0 ? 'No bills yet.' : 'No bills match.'}
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Sheet>
        )}
        <Typography level="body-xs" textColor="neutral.500">
          Archived bills are the old subcontractor invoices, kept as recorded. They are already in FreeAgent as subcontractor payments, so they are never sent. Subcontractors and suppliers are managed under <Link to="/directory">Directory → Accounts</Link>.
        </Typography>
      </Stack>

      <BillFormModal
        open={formOpen}
        bill={editing}
        accounts={accounts}
        billToCompanies={billToCompanies}
        onAccountCreated={(account) => setAccounts((current) => [...current, account])}
        onClose={() => setFormOpen(false)}
        onSaved={afterSave}
      />
      <SendBillModal
        bill={sending}
        onClose={() => setSending(null)}
        onSent={(bill) => { setSending(null); replace(bill); }}
      />
      <MarkPaidModal
        bill={paying}
        onClose={() => setPaying(null)}
        onSaved={(bill) => { setPaying(null); replace(bill); }}
      />
    </Box>
  );
};

export default BillsPage;
